import { Tie } from '../_shared/models/tie.model';
import { TieDetailsService } from './../ties/tie-details/tie-details.service';
import { ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { KeepAwake } from '@capacitor-community/keep-awake';
import { Capacitor } from '@capacitor/core';
import { Platform, SegmentCustomEvent, SegmentValue } from '@ionic/angular';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { AuthService } from '../auth/auth.service';
import { BoardHelpService } from '../match-tracker/tracker-board/board-help.service';
import { TrackerBoardComponent } from '../match-tracker/tracker-board/tracker-board.component';
import { TrackerBarComponent } from '../scoreboard/tracker-bar/tracker-bar.component';
import { LoaderService } from '../_core/services/loader.service';
import { MatchService } from '../_core/services/match.service';
import { ToastService } from '../_core/services/toast.service';
import { StopwatchComponent } from '../_shared/components/stopwatch/stopwatch.component';
import { MatchUpStatusEnum } from '../_shared/models/csTypes/enums/matchup_status';
import { WinReasonEnum } from '../_shared/models/csTypes/enums/win_reason';
import { InterruptionReason } from '../_shared/models/enums/interruption-reason';
import { Match } from '../_shared/models/match.model';
import { Set } from '../_shared/models/set.model';
import { MatchTrackerService } from '../match-tracker/match-tracker.service';
import { ThemeService } from '../_core/services/theme.service';
import { InfringementListComponent } from './infringement-list/infringement-list.component';
import { ScreenOrientation } from '@capacitor/screen-orientation'
import { RefresherCustomEvent } from '@ionic/angular';
import { StartMatchService } from '../match-tracker/start-match/start-match.service';
import { ScoreFormat } from '../_shared/models/score-format.model';
import { ClockName } from '../_shared/models/enums/clock-name.enum';
import { Clock } from '../_shared/models/clock.model';
import { OfficialViewService } from './official-view.service';
import { InfringementService } from './infringement-list/infringement.service';
import { ScoreService } from '../match-tracker/tracker-board/services/score.service';

@Component({
  standalone: false,
    selector: 'app-official-view',
    templateUrl: './official-view.page.html',
    styleUrls: ['./official-view.page.scss'],
})
export class OfficialViewPage implements OnInit, OnDestroy {
    private startMatchService = inject(StartMatchService);
    public authService = inject(AuthService);
    public officialViewService = inject(OfficialViewService);

    @ViewChild('violations') violationsRow: ElementRef | undefined;
    @ViewChild('header', { read: ElementRef }) header: ElementRef | undefined;
    @ViewChild('playerBox', { read: ElementRef }) playerBox: ElementRef | undefined;
    @ViewChild('ticker', { read: ElementRef }) ticker: ElementRef | undefined;
    @ViewChild('board') trackerBoardComponent: TrackerBoardComponent | undefined;
    @ViewChild('trackerBar') trackerBarComponent: TrackerBarComponent | undefined;
    @ViewChild('violationList') violationList: InfringementListComponent | undefined;
    @ViewChild('warmUpClock') warmUpClock: StopwatchComponent | undefined;
    @ViewChild('pointBreakClock') pointBreakClock: StopwatchComponent | undefined;
    @ViewChild('setTimeClock') setTimeClock: StopwatchComponent | undefined;

    public readonly clockName : typeof ClockName = ClockName;

    match!: Match;
    tie: Tie | undefined;

    scoreFormat: ScoreFormat;
    private matchId!: string;

    rowStyle: any;

    isOwner = false;
    isTracker = false;
    isDoubleFault = false;
    isReverse = false;
    selectedSegment: SegmentValue = 'scoring';

    canViewOnDevice = false;
    isTablet = false;
    isMobile = false;

    numberOfRows: number = 3;
    rowHeight!: number;
    sideColors = ['#3f51b5', '#009688'];
    private readonly destroy$: Subject<void> = new Subject();
    headerHeight: any;


    constructor(
        private activatedRoute: ActivatedRoute,
        private loaderService: LoaderService,
        private matchService: MatchService,
        public bhs: BoardHelpService,
        public platform: Platform,
        private cd: ChangeDetectorRef,
        private toast: ToastService,
        private matchTrackerService: MatchTrackerService,
        private themeService: ThemeService,
        private tieDetailsService: TieDetailsService,
        private infringementService: InfringementService,
        private scoreService: ScoreService,
    ) { }


    async ngOnInit() {
        await this.officialViewInit();

        this.matchId = this.activatedRoute.snapshot.params['id'];
        this.getMatch();
        this.subscriptions();
    }

    async ngOnDestroy() {
        if (Capacitor.isNativePlatform()) {
            if (await Capacitor.isPluginAvailable('KeepAwake')) {
                await KeepAwake.allowSleep();
            }

            if (this.isTablet) {
                await ScreenOrientation.unlock()
            }
        }

        this.destroy$.next();
        this.destroy$.complete();
    }

    private async officialViewInit() {
        if (this.platform.is('android') || this.platform.is('ios')) {
            this.isTablet = this.platform.is('tablet');
            this.isMobile = !this.isTablet;
            if (Capacitor.isNativePlatform() && this.isTablet && Capacitor.isPluginAvailable('ScreenOrientation')) {
                await ScreenOrientation.lock({ orientation: 'landscape'})
            }

            if (Capacitor.isNativePlatform() && await Capacitor.isPluginAvailable('KeepAwake')) {
                await KeepAwake?.keepAwake();
            }

            if (this.themeService.isDarkMode) {
                this.sideColors = ['#03a9f4', '#8bc34a']
            }
            this.canViewOnDevice = true;
        }
    }

    async getMatch(refresher?: RefresherCustomEvent) {

        if (!refresher) {
            await this.loaderService.present();
        }

        this.matchService
            .show(this.matchId)
            .pipe(
                takeUntil(this.destroy$),
                finalize(async () => {
                    await this.loaderService.dismiss();
                    if (refresher) refresher.detail.complete();
                    this.getAvailableHeight();
                    this.updateOfficialView();
                })
            )
            .subscribe({
                next: async res => {
                    this.match = res.data;                    
                    if (this.match.tie) {
                        this.tie = await this.tieDetailsService.getTieInfo(this.match.tie, false, false, 'official-view ln146');
                        if (this.tie.sides[0].team?.colors.length) this.sideColors[0] = this.tie.sides[0].team?.colors[0];
                        if (this.tie.sides[1].team?.colors.length) this.sideColors[1] = this.tie.sides[1].team?.colors[0];

                        /**
                         * 🔁 Trigger tieMatches observable and auto-unsubscribe on destroy
                         */
                        this.tieDetailsService
                        .getMatches(this.match.tie, true)
                        .pipe(takeUntil(this.destroy$))
                        .subscribe((matches) => {
                            // no action, just need to subscribe to get updated data.
                        });
                    }

                    this.scoreFormat = this.bhs.getScoreFormat(this.match.matchFormat, this.match.discipline, this.match.score);
                    this.numberOfRows = this.scoreFormat.numberOfSets > 3 ? this.scoreFormat.numberOfSets : 3;

                    this.isOwner = this.match.dist.owner === this.authService.authUserSignal()?._id;
                    this.isTracker = this.matchService.isTracker(this.match);
                }
            });
    }

    private updateOfficialView() {
        this.isReverse = this.bhs.getLastPoint(this.match)?.extensions?.find(ext => ext.name === 'areSidesReversed')?.value;
        this.officialViewService.areSidesReversed = this.isReverse;
        this.rowStyle = this.matchTrackerService.getRowStyle(this.sideColors, this.isReverse);
        this.cd.detectChanges();
    }

    swapSides() {
        if (this.match.matchStatus !== 'TBP') {
            this.isReverse = !this.isReverse;
            if (this.bhs.getLastPoint(this.match)?.extensions?.find(ext => ext.name === 'areSidesReversed')) {
                this.bhs.getLastPoint(this.match)!.extensions!.find(ext => ext.name === 'areSidesReversed')!.value = this.isReverse;
            }
            this.rowStyle = this.matchTrackerService.getRowStyle(this.sideColors, this.isReverse);
        }
    }

    getGameScore(set: Set, sideNumber: number): number {
        return set[`side${sideNumber}Score`];
    }

    addPoint(pointModel: { sideNumber: number, point?: number, winReason?: WinReasonEnum,  }) {
        if (!this.isCompleted()) {
            this.trackerBoardComponent!.addPoint(pointModel.sideNumber, 'NONE', pointModel.sideNumber, 'SE', 'I', 'BL', null, pointModel.point);
            this.resetAfterPoint();
        }
    }

    addGame(gameModel: { sideNumber: number, winReason?: WinReasonEnum }) {
        if (!this.isCompleted() && (!this.trackerBarComponent!.isPointByPoint || gameModel.winReason === WinReasonEnum.PENALTY)) {
            this.trackerBoardComponent!.addGame(gameModel.sideNumber, gameModel.winReason);
            this.resetAfterPoint();
        }
    }

    ace() {
        if (!this.isCompleted()) {
            const sideNumber = this.match.score!.server!.sideNumber;
            this.trackerBoardComponent!.ace(sideNumber);
            this.resetAfterPoint();
        }
    }

    fault(shotOutcome: string) {
        if (!this.isCompleted()) {
            const sideNumber = this.match.score!.server!.sideNumber as number;

            if (!this.isDoubleFault) {
                this.trackerBoardComponent!.firstServeError(sideNumber, shotOutcome);
                this.isDoubleFault = !this.isDoubleFault;
                this.pointBreakClock!.clearClock();
            } else {
                this.trackerBoardComponent!.doubleFault(sideNumber, shotOutcome);
                this.resetAfterPoint();
            }
        }
    }

    undo() {
        this.isDoubleFault = false;
        this.trackerBoardComponent!.undo();
    }

    private resetAfterPoint() {
        this.isDoubleFault = false;
        this.pointBreakClock?.clearClock();
        this.pointBreakClock?.startClock();
        this.setTimeClock?.updateClockOverSocket();
    }

    getWinningSetColor(set: Set, sideNumber: number): string | null {
        if (set.winningSide) {
            if (set.winningSide === sideNumber) {
                return this.sideColors[sideNumber - 1];
            } else {
                return '#808080'
            }
        }

        return null;
    }

    private getAvailableHeight() {
        setTimeout(() => {
            this.rowHeight = this.getRowHeight();
        });
    }

    async moreOptions() {
        await this.trackerBarComponent!.showMoreOptions();
    }

    getRowHeight(): number {
        const windowHeight: number = this.platform.height();
        this.headerHeight = this.header?.nativeElement.offsetHeight;

        const violationsRow = this.violationsRow?.nativeElement?.offsetHeight || 0;
        const playerBox = this.playerBox?.nativeElement?.offsetHeight || 0;
        const ticker = this.ticker?.nativeElement?.offsetHeight || 0;

        return this.isTablet
            ? (windowHeight - this.headerHeight - playerBox - violationsRow - ticker) / 2
            : (windowHeight - this.headerHeight - playerBox - ticker) / 3.5
    }

    isCompleted(): boolean {
        return this.match.matchStatus === MatchUpStatusEnum.COMPLETED;
    }

    isInterrupted(): boolean {
        return this.match.matchStatus === MatchUpStatusEnum.RETIRED ||
            this.match.matchStatus === MatchUpStatusEnum.DEFAULTED ||
            this.match.matchStatus === MatchUpStatusEnum.INCOMPLETE ||
            this.match.matchStatus === MatchUpStatusEnum.ABANDONED ||
            this.match.matchStatus === 'SUSPENDED';
    }

    async resumeSuspendedMatch() {
        await this.loaderService.present();
        const suspendedTimeItems = this.match.timeItems?.filter(timeItem => timeItem.itemValue.interruptionReason === InterruptionReason.MATCH_SUSPENSION);
        this.match.matchStatus = MatchUpStatusEnum.IN_PROGRESS;
        this.match.score = this.scoreService.getScoreObject(this.match);
        if (suspendedTimeItems?.length) {
            suspendedTimeItems[0].itemValue.duration = new Date().getTime() - new Date(suspendedTimeItems[0].itemDate!).getTime();
           
            await this.matchService
                .updateTimeItem(this.match._id, suspendedTimeItems[0].itemId, suspendedTimeItems[0].itemValue)
                .pipe(takeUntil(this.destroy$))
                .subscribe({
                    next: res =>{
                        this.toast.presentToast(res.message);
                    }
                });
        }

        await this.matchService
            .update(this.match, false)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: async () =>{
                    this.matchService.publishEdit(this.match);
                    this.matchService.publishScoreUpdated(this.match);
                    await this.loaderService.dismiss();
                }
            })
    }

    segmentChanged(ev: SegmentCustomEvent) {
        this.selectedSegment = ev.detail.value!;
    }

    async subscriptions() {
        this.matchService
            .subscribeScoreUpdated()
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                (match: Match) => {
                    if (this.match._id === match._id) {
                        this.match = {...this.match, ...match};
                        this.updateOfficialView();
                        this.getAvailableHeight()
                    }

                }
            );
        this.startMatchService
            .subscribeStartMatch()
            .pipe(
                takeUntil(this.destroy$))
            .subscribe(
                startMatch => {
                    if (this.match._id === startMatch.matchId && startMatch.matchActive) {
                        this.trackerBarComponent!.changeScoringType(true);
                        this.updateOfficialView();
                        this.getAvailableHeight()
                    }
                }
            );

            this.infringementService
              .subscribeUpdateViolations()
              .pipe(takeUntil(this.destroy$))
              .subscribe(
                match => {
                  if (this.match._id === match._id) {
                    this.match = {...this.match,
                        timeItems: match?.timeItems
                    };
                }
                }
              )

        this.platform.resize.subscribe(
            r => {
                if (this.platform.isLandscape()) {
                    this.getAvailableHeight();
                }
            }
        )

        // Subscribe to the swapSides event
        await this.officialViewService.swapSides$
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
            this.swapSides();
        });
    }

    async addOverrule(sideNumber: number) {
        await this.violationList!.addOverrule(sideNumber);
    }

    async addTimeViolation(sideNumber: number) {
        await this.violationList!.addTimeViolation(sideNumber);
    }

    async addInterruption(sideNumber: number) {
        await this.violationList!.addInterruption(sideNumber);
    }

    async addCodeViolation(sideNumber: number) {
        await this.violationList!.addCodeOfConduct(sideNumber);
    }

    getTeam(sideNumber: number) {
        return this.tie?.sides[sideNumber - 1].team;
    }

    getClock(clockName: ClockName): Clock | undefined {
        return this.match.clocks.find(c => c.name === clockName);
    }

    getLastPoint() {
        return this.bhs.getLastPoint(this.match);
    }

    getLastGame() {
        return this.bhs.getLastGame(this.match);
    }
}
